/**
 * ============================================================================
 * PERFORMANCE METRICS — client-side observability for the reporting UI.
 * ============================================================================
 *
 * WHY: the 2026-08 WBR latency investigation had ZERO client-side signal —
 * every number had to be reconstructed from API Gateway access logs, which
 * cannot see render time or the user-experienced waterfall. This module makes
 * the four findings of that investigation continuously measurable:
 *
 *   Api.Latency / Api.Fault    — per-operation latency and errors as the
 *                                CLIENT experiences them (incl. network RTT,
 *                                which server metrics cannot see).
 *   Chain.Pages/Chain.Duration — pagination chain cost (the "5.7 serial pages
 *                                per week" finding).
 *   Window.TimeToComplete      — time until the render gate opens: the exact
 *                                "UI loaded but data still blank" duration.
 *   Page.* web vitals          — shell phase (TTFB, FCP, LCP).
 *
 * TRANSPORT: the existing KatalMonitoring instance (src/monitoring.ts) —
 * Mons → monitoring API Gateway → CloudWatch + Sushi. Already deployed; this
 * module only adds emitters.
 *
 * RULES:
 * - Metrics must NEVER break the app: every emitter is try/caught.
 * - Dimensions stay LOW-CARDINALITY (operation names, dataMode — never week
 *   tokens, DCR ids, or user input): CloudWatch bills and aggregates per
 *   dimension-value combination.
 */

import { monitor } from 'src/monitoring';

/** '/getMetrics' → 'GetMetrics'; unknown paths collapse to 'Other' (cardinality guard). */
const KNOWN_OPERATIONS = new Set([
  'GetMetrics',
  'ListMetricComments',
  'PublishMetricComment',
  'GetMetricDefinitions',
  'GetWeekNumbers',
  'GetPermissions',
  'GetDcrSummary',
  'GetDcrServiceOrderSummary',
  'ListDashboards',
  'GenerateEmbedUrlForChatAgent',
]);

export function operationFromPath(path: string): string {
  const name = path.replace(/^\//, '').split('?')[0];
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  return KNOWN_OPERATIONS.has(pascal) ? pascal : 'Other';
}

/** One API round trip, as experienced by the browser (network + server + parse). */
export function recordApiCall(
  path: string,
  durationMs: number,
  ok: boolean,
  status?: number,
): void {
  try {
    const operation = operationFromPath(path);
    monitor.time('Api.Latency', Math.round(durationMs), { operation });
    monitor.count('Api.Call', 1, { operation });
    if (!ok) {
      monitor.count('Api.Fault', 1, {
        operation,
        status: String(status ?? 0),
      });
    }
  } catch {
    /* metrics never break the app */
  }
}

/**
 * The auth-cache clamp actually repaired a stale stamp (see
 * accessors/sentryAuthCacheClamp.ts). Counts thrash-window encounters:
 * without the clamp each of these would have been a serialized /sso/login
 * probe ahead of every subsequent request wave.
 */
export function recordClampRepair(): void {
  try {
    monitor.count('Auth.ClampRepair', 1);
  } catch {
    /* metrics never break the app */
  }
}

/**
 * One COMPLETED pagination chain (getMetricsAllPages / fetchAll): how many
 * serial round trips one logical fetch cost, and how long the whole chain
 * took. Deliberately not emitted on failure or page-cap abort: a partial
 * chain would skew the Chain.Pages distribution downward, and the failing
 * page is already counted by Api.Fault at the transport layer.
 */
export function recordPaginationChain(
  operation: 'GetMetrics' | 'ListMetricComments' | 'GetDcrSummary' | 'GetDcrServiceOrderSummary',
  pages: number,
  durationMs: number,
): void {
  try {
    monitor.time('Chain.Duration', Math.round(durationMs), { operation });
    monitor.count('Chain.Pages', pages, { operation });
  } catch {
    /* metrics never break the app */
  }
}

/**
 * The render gate opening: every visible week loaded and stitched. This is the
 * user-facing "time until data appears" for a window, measured from the moment
 * the window's weeks were requested.
 */
export function recordWindowComplete(
  dataMode: string,
  weekCount: number,
  durationMs: number,
): void {
  try {
    monitor.time('Window.TimeToComplete', Math.round(durationMs), { dataMode });
    monitor.count('Window.Weeks', weekCount, { dataMode });
  } catch {
    /* metrics never break the app */
  }
}

/** Idempotence guard: web vitals are per-pageview, emit once. */
let vitalsInitialized = false;

/**
 * Shell-phase vitals: TTFB from Navigation Timing; FCP and LCP from
 * PerformanceObserver. Called once at bootstrap (AppInitWrapper).
 */
export function initWebVitals(): void {
  if (vitalsInitialized) return;
  vitalsInitialized = true;
  try {
    const [nav] = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[];
    if (nav && nav.responseStart > 0) {
      monitor.time('Page.TTFB', Math.round(nav.responseStart));
    }

    new PerformanceObserver((list, observer) => {
      try {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            monitor.time('Page.FCP', Math.round(entry.startTime));
            observer.disconnect();
          }
        }
      } catch {
        /* async callback — the outer try/catch cannot cover it */
      }
    }).observe({ type: 'paint', buffered: true });

    // LCP finalizes when the page is backgrounded or unloaded; report the
    // latest candidate once, on whichever of the two signals fires first
    // (visibility→hidden covers tab switches; pagehide covers navigations
    // and unloads that skip the visibilitychange event on some browsers).
    let lcpMs: number | undefined;
    let lcpReported = false;
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) lcpMs = Math.round(last.startTime);
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    const reportLcp = () => {
      try {
        if (lcpReported || lcpMs === undefined) return;
        lcpReported = true;
        monitor.time('Page.LCP', lcpMs);
        lcpObserver.disconnect();
        document.removeEventListener('visibilitychange', onVisibilityHidden);
        window.removeEventListener('pagehide', reportLcp);
      } catch {
        /* async callback — the outer try/catch cannot cover it */
      }
    };
    const onVisibilityHidden = () => {
      if (document.visibilityState === 'hidden') reportLcp();
    };
    document.addEventListener('visibilitychange', onVisibilityHidden);
    window.addEventListener('pagehide', reportLcp);
  } catch {
    /* PerformanceObserver types vary by browser; vitals are best-effort */
  }
}
